// src/components/SmoothScrollWrapper.jsx

import { useEffect } from 'react';
import { ReactLenis, useLenis } from '@studio-freight/react-lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Dispatches a custom event once the Lenis↔ScrollTrigger proxy is active.
// Any component can listen for 'lenis:ready' on window to safely create
// ScrollTrigger instances that depend on the correct scroll position.
const LENIS_READY_EVENT = 'lenis:ready';

function LenisScrollTriggerBridge() {
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return lenis.scroll;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
    });

    const onScroll = () => ScrollTrigger.update();
    lenis.on('scroll', onScroll);

    // Refresh once proxy is wired up, then signal readiness
    ScrollTrigger.refresh();
    window.dispatchEvent(new CustomEvent(LENIS_READY_EVENT));

    return () => {
      lenis.off('scroll', onScroll);
      // Do NOT ScrollTrigger.getAll().kill() here — that destroys triggers owned
      // by child components (ParagraphReveal, HeroTitleReveal, etc.) when this
      // effect re-runs (e.g. lenis identity change) while children stay mounted.
      // Children clean up their own triggers on unmount via gsap.context.revert().
      ScrollTrigger.clearScrollMemory();
      ScrollTrigger.scrollerProxy(document.body, null);
    };
  }, [lenis]);

  return null;
}

export { LENIS_READY_EVENT };

export default function SmoothScrollWrapper({ children }) {



  
  
  useEffect(() => {
    // Usercentrics hides its UI inside a Shadow DOM, so standard querySelector won't find it.
    // This observer finds the shadow root and adds data-lenis-prevent directly to the modal.
    const observedShadowHosts = new Set();
    const shadowObservers = [];

    // Helper to check for the modal and add the attribute safely
    const injectPreventAttribute = (shadowRoot) => {
      try {
        if (!shadowRoot || typeof shadowRoot.querySelector !== 'function') return;
        const dialog = shadowRoot.querySelector('#uc-main-dialog');
        if (dialog && !dialog.hasAttribute('data-lenis-prevent')) {
          dialog.setAttribute('data-lenis-prevent', 'true');
        }
      } catch (e) {
        // Silently swallow shadow DOM query errors
      }
    };

    let bodyObserver;
    try {
      bodyObserver = new MutationObserver(() => {
        try {
          const shadowHosts = Array.from(document.body.querySelectorAll('*')).filter(el => el && el.shadowRoot);
          
          for (const host of shadowHosts) {
            if (host && host.shadowRoot && !observedShadowHosts.has(host)) {
              observedShadowHosts.add(host);
              
              // Check immediately
              injectPreventAttribute(host.shadowRoot);

              // Crucial: Watch INSIDE the Shadow DOM for dynamic changes (like opening the cookie settings)
              const shadowObserver = new MutationObserver(() => injectPreventAttribute(host.shadowRoot));
              shadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
              shadowObservers.push(shadowObserver);
            }
          }
        } catch (e) {
          // Prevent MutationObserver callback errors from bubbling
        }
      });
      
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      // Prevent observation failure from crashing React
    }
    
    return () => {
      try {
        if (bodyObserver) bodyObserver.disconnect();
        shadowObservers.forEach(obs => {
          try { obs.disconnect(); } catch (e) {}
        });
      } catch (e) {}
    };
  }, []);

  return (
    <ReactLenis
      root
      options={{
        duration: .2,
        lerp: 0.05,
        smoothWheel: true,
        smoothTouch: true,
      }}
    >
      <LenisScrollTriggerBridge />
      {children}
    </ReactLenis>
  );
}
